import { useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  NameAvatar,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  TuteroLogo,
  useSidebar,
} from '@tutero/ui';

import { signOut } from '@/lib/auth';

import { ClusterPicker } from './ClusterPicker';
import { isSuperUser } from './permissions/staff';
import { useStaff } from './session';
import { SettingsSheet } from './settings/SettingsSheet';
import { QuickSearchButton, QuickSearchHost } from './quick-search/QuickSearch';
import { isLink } from './tool';
import { sectionsFor, type NavRow, type TabbedPage } from './tools';

/**
 * The nav (`manifests-draw-the-nav` D1): the published `Sidebar`, arranged — a group per
 * section, a row per tool, the account menu in the footer, and the page in the
 * `SidebarInset`. Collapse and its remembered state, the scroll, the phone's offcanvas
 * sheet and the keyboard shortcut are all the component's. The nav sits on its own dark
 * ground (D1a), so where a published part reaches for a page token this file names the
 * sidebar family at the call site; the component itself is untouched (A5).
 */
export function AppSidebar({ children }: { children: ReactNode }) {
  const staff = useStaff();
  const sections = sectionsFor(staff.perms);
  return (
    /*
     * 260px, which is Flutter's own `_sidebarWidth` (`sidebar.dart:32`). The design
     * system's token is 200px, sized for schools' column (`schools_main_layout.dart`),
     * and the admin's tool names are longer than a school's — at 200 *Customer Success
     * Agent*, *Account Management Agent* and *Change Wrong Email* all truncate.
     *
     * Set here rather than in the token because it is this app's column, not the
     * system's: `SidebarProvider` puts the host's `style` last so this wins (A5 — the
     * component is untouched).
     */
    <SidebarProvider style={{ '--sidebar-width': '260px' } as CSSProperties}>
      {/*
       * Collapsed, the nav keeps a rail of its tools' icons, its toggle at the top: the toggle
       * lives in the nav, never on the page, and a closed nav is still one press from open.
       * Its edge is the nav's own colour: a light line shows against a dark embedded app.
       */}
      <Sidebar collapsible="icon" className="border-sidebar">
        <SidebarHeader className="px-4 py-3 group-data-[collapsible=icon]:px-2">
          <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center">
            {/* The lockup and the picker alone: a word between them overflows the column (D7). */}
            <Link
              to="/"
              aria-label="Admin home"
              className="flex items-center group-data-[collapsible=icon]:hidden"
            >
              <TuteroLogo height={28} color="white" alt="" />
            </Link>
            <div className="flex items-center gap-1">
              <div className="group-data-[collapsible=icon]:hidden">
                <ClusterPicker />
              </div>
              <SidebarTrigger className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground" />
            </div>
          </div>
        </SidebarHeader>
        {/* No scrollbar: the column still scrolls under a wheel or a swipe, as the Flutter one does. */}
        <SidebarContent className="gap-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {/* Quick Search, above the tools it reaches past; ⌘K / Ctrl+K opens the same box. Out of the header, which holds the lockup and the picker alone (D7). */}
          <SidebarGroup className="px-1.5 pt-1 pb-2">
            <QuickSearchButton />
          </SidebarGroup>
          <nav aria-label="Tools">
            {sections.map(({ id, title, tools }) => (
              /* Open on first load (D8): a staff member holding one section's
                 grants sees that section and no other, so nothing is buried. */
              <Collapsible key={id} defaultOpen className="group/section">
                <SidebarGroup className="border-sidebar-border border-t px-1.5 pt-2 pb-1.5">
                  {/* `text-muted-foreground` is the component's; on navy it is unreadable (D1a). */}
                  <SidebarGroupLabel
                    asChild
                    className="text-sidebar-foreground text-button-m h-8 rounded-md px-2.5"
                  >
                    <CollapsibleTrigger className="w-full justify-between">
                      {title}
                      <Icon
                        name="keyboard_arrow_down"
                        size={20}
                        className="transition-transform group-data-[state=closed]/section:-rotate-90"
                      />
                    </CollapsibleTrigger>
                  </SidebarGroupLabel>
                  <CollapsibleContent>
                    <SidebarGroupContent>
                      <SidebarMenu className="gap-0 pt-1">
                        {tools.map((row) =>
                          row.kind === 'tabs' ? (
                            <TabbedRow key={row.id} page={row} />
                          ) : (
                            <ToolRow key={row.id} tool={row} />
                          ),
                        )}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </SidebarGroup>
              </Collapsible>
            ))}
          </nav>
        </SidebarContent>
        <SidebarFooter className="px-1.5 py-2">
          <AccountMenu />
        </SidebarFooter>
      </Sidebar>
      {/* A flex item will not shrink under its content: without this a wide table widens the whole page instead of scrolling inside its own box (D7). */}
      <SidebarInset className="min-w-0">
        {/* A phone's nav is a sheet, off the screen until asked for: its toggle stays on the page. */}
        <header className="flex h-12 shrink-0 items-center px-2 md:hidden">
          <SidebarTrigger />
        </header>
        {/* The page's gutters, dropped whole for a surface that is an embed, which takes the column edge to edge (`Embed`). */}
        <div className="flex min-w-0 flex-1 flex-col p-6 pt-0 has-data-[slot=embed]:p-0 md:pt-6">
          {children}
        </div>
      </SidebarInset>
      <QuickSearchHost />
    </SidebarProvider>
  );
}

/* The open tool reads blue on the navy, as the Flutter admin's does, rather than on a lit pill. */
const ROW =
  'text-chrome-regular-m data-[active=true]:text-blue-300 data-[active=true]:hover:text-blue-300 h-7.5 gap-2.5 rounded-md py-0 pr-1.5 pl-2.5 data-[active=true]:bg-transparent data-[active=true]:hover:bg-sidebar-accent';

/** Choosing a row dismisses the phone's sheet; on a desktop there is none. */
function useChosen(): () => void {
  const { isMobile, setOpenMobile } = useSidebar();
  return () => {
    if (isMobile) setOpenMobile(false);
  };
}

/**
 * A page of tabs' row (`learning-backend-is-one-page` D2): it opens the first tab the session
 * holds, and reads as open on any of them, since each is at its own tool's address.
 */
function TabbedRow({ page }: { page: TabbedPage }) {
  const chosen = useChosen();
  const open = useRouterState({
    select: (state) =>
      page.tools.some(
        (tool) => tool.id === state.location.pathname.split('/')[1],
      ),
  });
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={page.title}
        isActive={open}
        className={ROW}
      >
        <Link to="/$tool" params={{ tool: page.tools[0].id }} onClick={chosen}>
          <Icon name={page.icon} set="classic" size={18} />
          <span>{page.title}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/**
 * A tool's row: a `Link` for a page, whose `activeProps` put `data-active` on the anchor
 * for the component's own styling to read (D5), and an outside anchor for a `link`, which
 * has no route and is never the open tool (D4).
 */
function ToolRow({ tool }: { tool: Exclude<NavRow, TabbedPage> }) {
  const chosen = useChosen();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={tool.title} className={ROW}>
        {isLink(tool) ? (
          <a
            href={tool.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={chosen}
          >
            {/* `classic` is the filled set Flutter's `Icons.*` draws: the old admin's glyphs. */}
            <Icon name={tool.icon} set="classic" size={18} />
            <span>{tool.title}</span>
          </a>
        ) : (
          <Link
            to="/$tool"
            params={{ tool: tool.id }}
            activeOptions={{ exact: false }}
            activeProps={{ 'data-active': 'true' }}
            onClick={chosen}
          >
            <Icon name={tool.icon} set="classic" size={18} />
            <span>{tool.title}</span>
          </Link>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/**
 * The footer's account menu (D7; `the-nav-lists-every-tool` D9–D14): the face or initial,
 * the name and the email on its button, and the rows of the Flutter avatar menu
 * (`admin-app/lib/legacy/shared/widgets/dropdowns/avatar_menu.dart`) — Settings as a popup,
 * User Permissions and the Leave Request Form at routes of the shell's own
 * (`permissions-is-in-the-profile` D1, D7, reversing `the-permissions-page` D1's nav row;
 * `leave-request-is-an-embed` D1, reversing D13's tab), and Log out. Its rows are inside
 * `DropdownMenuContent`, which is on the page's ground, so none of them names a sidebar
 * token (D1a).
 *
 * The menu does not name the account again: the button it opens from already does. Add
 * Profile Picture is a section of Settings rather than a row of its own, so everything about
 * the staff member's own account is in one place (`settings-is-a-dialog` D1).
 *
 * Exported for `account.test.tsx`: the menu's rows are the whole of what it tests, and
 * the nav around them needs a router.
 */
export function AccountMenu() {
  const staff = useStaff();
  const [settings, setSettings] = useState(false);
  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* A card, as the Flutter admin's footer is: bordered, a shade off the ground. */}
              <SidebarMenuButton
                size="lg"
                className="group/account border-sidebar-border bg-sidebar-accent h-13 gap-2 border px-2"
              >
                {/* Their own face where they have set one; the initial otherwise (`a-staff-member-has-a-face` D6). */}
                <NameAvatar name={staff.name} size="sm" src={staff.image} />
                <span className="flex min-w-0 flex-1 flex-col text-left">
                  <span className="text-chrome-bold-m truncate">
                    {staff.name}
                  </span>
                  {/* The row is on navy; the menu below it is on the page (D1a). */}
                  <span className="text-chrome-regular-s text-sidebar-foreground/70 truncate">
                    {staff.email}
                  </span>
                </span>
                <Icon
                  name="keyboard_arrow_up"
                  size={18}
                  className="transition-transform group-data-[state=open]/account:rotate-180"
                />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="top"
              align="start"
              /* The button's width, on the collapsed rail too narrow for its rows: 14rem at the least. */
              className="w-[max(var(--radix-dropdown-menu-trigger-width),14rem)]"
            >
              <DropdownMenuItem onSelect={() => setSettings(true)}>
                <Icon name="settings" size={16} />
                Settings
              </DropdownMenuItem>
              {/* Super Users only, as Flutter's menu. `lock`, not Flutter's `admin_panel_settings`, which the subset does not carry (D7). */}
              {isSuperUser(staff) ? (
                <DropdownMenuItem asChild>
                  <Link to="/permissions">
                    <Icon name="lock" size={16} />
                    User Permissions
                  </Link>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem asChild>
                <Link to="/leave-request">
                  <Icon name="calendar_today" size={16} />
                  Leave Request Form
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => void signOut()}
              >
                <Icon name="logout" size={16} />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <SettingsSheet open={settings} onOpenChange={setSettings} />
    </>
  );
}
